import { useCallback, useInsertionEffect, useRef } from 'react';

/**
 * A function that always runs the latest `handler` (its latest props and state) but never changes identity —
 * for effects that should call "whatever the current logic is" without re-running when that logic changes.
 *
 * This is what React's `useEffectEvent` is for. Do NOT use `useEffectEvent` in this codebase for now: in React
 * 19.2 (checked up to 19.2.8) it is only refreshed for plain function components. Inside a component wrapped in
 * `memo()` — nearly all of ours, and so every hook they call — it keeps the closure of the FIRST render forever.
 * That froze `useSyncProjectState`'s sync functions at "wall sketch properties still loading", so wall sketches
 * were saved but never reached the scene. (react-dom's commitBeforeMutationEffects updates `events` for fiber
 * tag 0 only; tags 11/15, forwardRef and memo, fall through.)
 *
 * The handler is swapped in an insertion effect, which runs for every component type and before the layout
 * and passive effects of the same commit — so an effect always calls the handler of its own render. The
 * returned function is stable: listing it in a dependency array (as exhaustive-deps asks) never re-runs anything.
 * Like an effect event, it is for effects and event handlers, not for calling during render.
 */
export const useStableEvent = <Args extends unknown[], Result>(handler: (...args: Args) => Result): ((...args: Args) => Result) => {
  const latest = useRef(handler);
  useInsertionEffect(() => {
    latest.current = handler;
  });
  return useCallback((...args: Args) => latest.current(...args), []);
};
